/**
 * 🔴🔴 identity-catalog (US-AI-0467-S01 · @UC9731 @UC9732 @UC9733 · thẻ T-28 / gap `G-30` · QĐ PO E-5)
 * — CÂU CHỮ trợ lý tự nói về MÌNH và về QUYỀN RIÊNG TƯ.
 *
 * > *"Goppy bán bằng niềm tin; một trợ lý không nói được **mình là ai** và **ai đọc được tin nhắn
 * > này** là chỗ mất niềm tin rẻ nhất."*
 *
 * ## Vì sao là FILE RIÊNG, không nhét vào ba catalog sẵn có
 * `index.md` §6 Q3 đề nghị "catalog câu chữ dùng chung" và trỏ sang chỗ `US-AI-0439` khai câu việc —
 * tức `orchestration/flow-catalog.ts`. Đo lại thì chỗ đó **không đúng ngữ nghĩa**, và đặt vào sẽ
 * kéo theo một hệ quả không ai muốn:
 *  1. `FLOW_CATALOG` là catalog **FLOW** — mỗi phần tử có `trigger`, `steps`, `configurable`.
 *     Câu danh tính không có trigger, không có bước, không phải một việc để làm.
 *  2. `configurable` cho phép admin bật/tắt từng flow. Nhét vào đó là **vô tình khiến câu tự-khai-AI
 *     tắt được bằng cấu hình** — trái thẳng `index.md` §6 Q5 (*"một trợ lý bị tắt khả năng tự khai
 *     mình là AI là thứ không nên cấu hình được"*).
 *  3. `AI_CAPABILITIES` nói *"làm được gì"*, `AI_CAPABILITY_CATALOG` là đơn vị bật/tắt usecase GHI —
 *     cả hai đều đã bị §6 Q3 loại.
 * ⇒ Một module nhỏ, một mối quan tâm. (Ghi ra đây để lần sau không ai "dọn dẹp" bằng cách gộp lại.)
 *
 * ## Một bộ chuẩn hoá, không phải hai
 * `resolveIdentityKind` nhận câu THÔ và tự chuẩn hoá bằng `normalizeViText` — bộ dùng chung của repo.
 * Nhận chuỗi "đã chuẩn hoá sẵn" từ caller là mời gọi hai bộ chuẩn hoá lệch nhau: lưới trung thực đo
 * bằng bộ A, router chạy bằng bộ B, và ngày chúng lệch thì lưới vẫn xanh còn người dùng nhận sai câu.
 *
 * ## Câu chữ phải ĐÚNG VỚI CODE, không phải đúng với mong muốn
 * `docs/ai-audit/05-*.md` §E là nguồn sự thật:
 *  · PII (số điện thoại/số tài khoản/email) **được che TRƯỚC khi rời hệ thống** — `base-llm-client.ts`,
 *    có test mutation-proof. Câu dưới đây mô tả đúng điều đó.
 *  · `rawPrompt` **ĐƯỢC LƯU THÔ** ở `ai_audit_log` (QĐ PO 27/07, có chủ đích: admin cần đọc câu gốc
 *    để phân tích câu trợ lý trượt). ⇒ **CẤM** nói *"mình không lưu gì cả"*. Câu dưới đây nói thẳng
 *    là **có lưu**, và lưu để làm gì.
 *  · Số tiền ẩn theo vai = `'•••'`, không phải `0`. ⇒ *"hiện theo đúng quyền của bạn"*.
 */

import { normalizeViText } from "./vi-normalizer.js";

/**
 * Câu trả lời TẤT ĐỊNH. 🔴 Đây là những câu mà **giá trị nằm ở việc đúng từng chữ** — xem
 * `VERBATIM_REPLIES` bên dưới: chúng **không** được một mô hình ngôn ngữ viết lại.
 */
export const IDENTITY_REPLIES = Object.freeze({
  /** Trợ lý tự khai là AI + ranh giới quyền hạn (không tự ghi tiền, sổ append-only). */
  SELF:
    "Mình là trợ lý AI của Goppy. Mình không tự ghi tiền — mọi khoản đều phải bạn bấm xác nhận, " +
    "và sổ thì chỉ ghi thêm chứ không xoá đi được.",
  /**
   * 🔴 Nói ĐÚNG thứ code làm, kể cả phần bất lợi. Vế *"câu bạn hỏi được lưu lại"* là CỐ Ý: nói
   * ngược lại sẽ dễ nghe hơn nhưng là nói dối (`05 §E` — `rawPrompt` lưu thô, QĐ PO 27/07).
   */
  PRIVACY:
    "Mình che số điện thoại, số tài khoản và email trước khi gửi câu hỏi đi xử lý. " +
    "Câu bạn hỏi thì được lưu lại, để đội vận hành xem lại khi mình trả lời trượt. " +
    "Còn số tiền thì mình chỉ hiện theo đúng quyền của bạn trong nhóm.",
} as const);

/** Nhóm ý định danh tính mà router phân giải được. `null` = không phải câu danh tính. */
export type IdentityKind = "self" | "privacy";

/**
 * Câu hỏi VỀ TRỢ LÝ (danh tính). Dạng KHÔNG DẤU (@AI-A11Y-02).
 *
 * ⚠️ Neo vào ĐẠI TỪ NGÔI HAI (`ban`/`may`/`tro ly`) + danh-từ-hỏi, KHÔNG nhận `la ai` trần: `la ai`
 * trần khớp cả *"thủ quỹ là ai"* (câu ĐỌC về người trong nhóm) — đúng ca mà `US-AI-0465` vừa vá.
 */
const IDENTITY_SELF_KW =
  /(ban ten gi|ban la ai|ban la gi|ban co phai nguoi|co phai nguoi that|ban co phai la nguoi|ban dung ai|ban la nguoi hay may|ban la con nguoi|tro ly la ai|ban la robot|ban co phai robot)/;

/**
 * Câu hỏi VỀ DỮ LIỆU / QUYỀN RIÊNG TƯ. Dạng KHÔNG DẤU.
 *
 * ⚠️ `an toan` phải đi kèm chủ thể DỮ LIỆU (`du lieu`/`thong tin`/`tin nhan`) — `an toan` trần khớp
 * cả câu hỏi về an toàn của tiền quỹ, là chuyện khác hẳn.
 */
const IDENTITY_PRIVACY_KW =
  /(ai doc duoc|ai xem duoc|ai nhin thay|doc tin nhan|xem tin nhan|luu tin nhan|du lieu cua toi|thong tin cua toi|du lieu co an toan|thong tin co an toan|tin nhan co an toan|co bao mat khong|bao mat khong|lo thong tin|ro ri)/;

/**
 * Phân giải nhóm câu danh tính. Hàm THUẦN, nhận câu THÔ (tự chuẩn hoá — xem docblock đầu file).
 *
 * 🔴 Kiểm QUYỀN RIÊNG TƯ TRƯỚC: *"bạn có lưu tin nhắn của tôi không"* chứa cả `ban` lẫn `luu tin nhan`
 * — người dùng đang hỏi về DỮ LIỆU, không phải hỏi trợ lý là ai. Trả nhầm câu tự-giới-thiệu cho một
 * câu hỏi về quyền riêng tư là né câu hỏi.
 */
export function resolveIdentityKind(message: string): IdentityKind | null {
  const norm = normalizeViText(message ?? "");
  if (IDENTITY_PRIVACY_KW.test(norm)) return "privacy";
  if (IDENTITY_SELF_KW.test(norm)) return "self";
  return null;
}

/**
 * 🔴🔴 US-AI-0467-S03 (@UC9732) — CÂU KHÔNG ĐƯỢC DIỄN GIẢI LẠI.
 *
 * `AI_VOICE_POLISH_ENABLED` mặc định BẬT ⇒ trên staging/production **mọi** `reply` đi qua một mô hình
 * ngôn ngữ trước khi tới người dùng. Chốt có ý nghĩa duy nhất của tầng đó là *"chữ số phải giữ
 * nguyên"* — mà câu quyền riêng tư **không có chữ số nào** ⇒ chốt đó **không chạm tới nó**.
 * Nghĩa đen: LLM được phép viết lại chính câu mà cả task này tồn tại để nói đúng sự thật, và một bản
 * "cho mượt" hoàn toàn có thể thành *"dữ liệu của bạn được mã hoá đầu cuối, không ai đọc được"*.
 *
 * ⇒ Loại trừ khỏi đường polish, **không** thêm bộ lọc cụm cấm ở đầu ra: bộ lọc là một danh sách tay
 * thứ hai, và một câu bịa KHÔNG dùng cụm cấm nào (*"tin nhắn của bạn chỉ mình bạn thấy"*) vẫn lọt.
 * Cùng tinh thần chốt sẵn có *"LLM viết CÂU, không viết SỐ"* — ở đây là **"LLM viết CÂU, không viết
 * CAM KẾT"**.
 *
 * 🔴 Đọc THẲNG từ `IDENTITY_REPLIES` chứ không chép chuỗi sang tầng API: sửa một dấu phẩy trong
 * catalog thì cả hai vế đổi cùng lúc. Một bản chép ở tầng API sẽ lệch **im lặng** — tính năng vẫn
 * chạy, chỉ mất lớp bảo vệ.
 */
export const VERBATIM_REPLIES: readonly string[] = Object.freeze(Object.values(IDENTITY_REPLIES));

/** `true` ⇒ lượt này phải tới người dùng NGUYÊN VĂN (không polish, không viết lại). */
export function isVerbatimReply(reply: string): boolean {
  return VERBATIM_REPLIES.includes(reply);
}

/**
 * 🔴 US-AI-0467-S02 (@UC9732) — CỤM CẤM: điều code KHÔNG làm, nên trợ lý KHÔNG được hứa.
 *
 * Danh sách là **DỮ LIỆU** để guard tự-khám-phá quét mọi câu trong `IDENTITY_REPLIES`; rỗng ⇒ guard
 * tự đỏ (self-check chống gate-mù). Mỗi cụm kèm lý do vì sao nó SAI với code hôm nay.
 */
export const FORBIDDEN_PRIVACY_PHRASES: readonly string[] = Object.freeze([
  "khong luu", // SAI: `rawPrompt` lưu thô ở `ai_audit_log` (QĐ PO 27/07)
  "khong ai doc duoc", // SAI: SUPER_ADMIN đọc được câu gốc — đó là mục đích của việc lưu
  "ma hoa dau cuoi", // SAI: không có end-to-end encryption ở đường này
  "khong bao gio roi", // SAI: câu hỏi ĐƯỢC gửi tới nhà cung cấp LLM (đã che PII, nhưng vẫn rời)
  "xoa vinh vien", // SAI: bảng append-only, không xoá được
  "an danh hoan toan", // SAI: `ai_audit_log` gắn với actor
  "chi minh ban thay", // SAI: cùng lý do "không ai đọc được"
  "tuyet doi an toan", // Hứa tuyệt đối — không hệ thống nào nói được câu này
]);
